import { FullOptions, RequestOptions } from 'parse';

import UserModel from 'src/models/UserModel';
import environmentStore from 'src/stores/EnvironmentStore';
import { EVENTS, pubsub } from 'src/util/pubsub';

let sessionExpiredCount = 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getRequestEndpoint = (path: unknown): string | undefined => {
  if (typeof path !== 'string') return undefined;

  const withoutQuery = path.split('?')[0];
  const withoutOrigin = withoutQuery.replace(/^https?:\/\/[^/]+/, '');
  const segments = withoutOrigin.split('/').filter(Boolean);
  const [first, second] = segments[0] === 'parse' ? segments.slice(1) : segments;

  return first === 'classes' && second ? `classes/${second}` : first;
};

const hasSessionToken = (args: unknown[]): boolean =>
  args.some(
    (arg) =>
      isRecord(arg) &&
      (typeof arg.sessionToken === 'string' || typeof arg['X-Parse-Session-Token'] === 'string')
  );

const handleError = (error: Error & Parse.Error, args: unknown[]): undefined | void => {
  if (environmentStore.isSnapshot()) {
    return;
  }

  const errorLabel = error.code ? `parse-response-${error.code}` : `parse-response-unknown`;
  UserModel.getInstance().trackError(errorLabel, error, {
    requestMethod: typeof args[0] === 'string' ? args[0] : undefined,
    requestEndpoint: getRequestEndpoint(args[1]),
    hasSessionToken: hasSessionToken(args),
    priorSessionExpiredCount: sessionExpiredCount
  });

  switch (error.code) {
    case 100:
      pubsub.emit(EVENTS.CONNECTIVITY_ERROR, `${error.code}: ${error.message}`);
      throw error;
    case 209:
      sessionExpiredCount += 1;
      pubsub.emit(EVENTS.SESSION_EXPIRED);
      break;
    default:
      throw error;
  }
};

interface AjaxResponse {
  response: { results: any[] };
  status: number;
  xhr: XMLHttpRequest;
}

type AjaxMethod = (
  method: string,
  url: string,
  data: any,
  headers?: any,
  options?: FullOptions
) => Promise<AjaxResponse>;

type RequestMethod = (
  method: string,
  path: string,
  data: any,
  options?: RequestOptions
) => Promise<AjaxResponse>;

interface RESTController {
  ajax: AjaxMethod;
  request: RequestMethod;
}

const wrapRESTMethod: <T extends Function>(
  method: T
) => (...args: any[]) => Promise<AjaxResponse> = (method) =>
  async function wrappedRESTMethod(...args) {
    try {
      return await method.call(null, ...args);
    } catch (error) {
      handleError(error, args);
    }
  };

export const wrapRESTController = (controller: RESTController) => ({
  ajax: wrapRESTMethod<AjaxMethod>(controller.ajax),
  request: wrapRESTMethod<RequestMethod>(controller.request)
});
